/**
 * @file ReportsListPage.js
 * @description Phase 1 Reports module: dummy report list with sorting, filtering, and row selection.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import '../page-list/PageList.css';

import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { FilterModalV2 } from '../helpers/FilterComponent_V2';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import { ROUTE_BUILDERS, ROUTES } from '../../constants/routes';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import { fetchReportDefinitions } from '../../services/reportingService';
import { getStatusLabelFromReportStatusId } from './reportUtils';
import { formatDate } from '../helpers/DateUtils';
import { getText } from '../../i18n';

function getOutputFormatLabel(allowedOutputFormats) {
    const formatNames = (allowedOutputFormats || [])
        .map(format => String(format?.name || '').trim())
        .filter(Boolean);

    if (formatNames.length > 0) {
        return formatNames.join(', ');
    }

    const fallbackCodes = (allowedOutputFormats || [])
        .map(format =>
            String(format?.code || '')
                .trim()
                .toUpperCase(),
        )
        .filter(Boolean);

    if (fallbackCodes.length > 0) {
        return fallbackCodes.join(', ');
    }

    return 'CSV';
}

function getFilteredReports(reports, searchTerm, selectedOptions) {
    const normalizedTerm = String(searchTerm || '')
        .trim()
        .toLowerCase();
    const formatSelections = selectedOptions?.['allowedOutputFormats'] || [];

    return reports.filter(report => {
        const matchesSearch =
            !normalizedTerm ||
            String(report.name || '')
                .toLowerCase()
                .includes(normalizedTerm) ||
            String(report.description || '')
                .toLowerCase()
                .includes(normalizedTerm);

        const matchesFormat =
            formatSelections.length === 0 ||
            formatSelections.includes(
                getOutputFormatLabel(report.allowedOutputFormats),
            );

        return matchesSearch && matchesFormat;
    });
}

function sortReports(reports, sortConfig) {
    if (!sortConfig?.key) {
        return reports;
    }

    const { key, direction } = sortConfig;

    return [...reports].sort((a, b) => {
        let aVal = a?.[key];
        let bVal = b?.[key];

        if (key === 'allowedOutputFormats') {
            aVal = getOutputFormatLabel(aVal);
            bVal = getOutputFormatLabel(bVal);
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            const cmp = aVal.localeCompare(bVal, undefined, {
                sensitivity: 'base',
            });
            return direction === SortDirections.ASCENDING ? cmp : -cmp;
        }

        if (aVal < bVal) {
            return direction === SortDirections.ASCENDING ? -1 : 1;
        }

        if (aVal > bVal) {
            return direction === SortDirections.ASCENDING ? 1 : -1;
        }

        return 0;
    });
}

function ReportsListPage() {
    const [reports, setReports] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const [activeModal, setActiveModal] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});

    const { sortConfig, renderSortArrow } = useTableSort(
        'name',
        SortDirections.ASCENDING,
    );

    const filteredReports = useMemo(
        () => getFilteredReports(reports, searchTerm, selectedOptions),
        [reports, searchTerm, selectedOptions],
    );

    const sortedReports = useMemo(
        () => sortReports(filteredReports, sortConfig),
        [filteredReports, sortConfig],
    );

    useEffect(() => {
        let isCancelled = false;

        async function loadReportList() {
            setIsLoading(true);

            try {
                const definitions = await fetchReportDefinitions();

                if (isCancelled) {
                    return;
                }

                setReports(definitions || []);
            } catch (error) {
                if (!isCancelled) {
                    setReports([]);
                    Toast.error(getText('msg_70a640360576'));
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        }

        loadReportList();

        return () => {
            isCancelled = true;
        };
    }, []);

    function handleSearchChange(event) {
        setSearchTerm(event.target.value);
    }

    function openModal(column) {
        setActiveModal(column);
    }

    function closeModal() {
        setActiveModal(null);
    }

    function handleApplyFilter(column, values) {
        setSelectedOptions(prev => ({
            ...prev,
            [column]: values,
        }));
    }

    function renderFilterModal(column, options) {
        const isSelected = Boolean(
            selectedOptions[column] && selectedOptions[column].length > 0,
        );

        return (
            <>
                <span
                    onClick={() => openModal(column)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            openModal(column);
                        }
                    }}
                >
                    {isSelected ? (
                        <svg
                            style={{
                                marginTop: '4px',
                                display: 'inline-block',
                                width: '1em',
                                height: '1em',
                            }}
                        >
                            <path
                                d="M1.08871 2.81875C0.701376 2.38585 0.507709 2.1694 0.500404 1.98544C0.494058 1.82564 0.562731 1.67205 0.686053 1.57022C0.828016 1.453 1.11846 1.453 1.69935 1.453H9.30065C9.88154 1.453 10.172 1.453 10.3139 1.57022C10.4373 1.67205 10.5059 1.82564 10.4996 1.98544C10.4923 2.1694 10.2986 2.38585 9.91129 2.81875L6.98903 6.0848C6.91182 6.17109 6.87322 6.21424 6.84569 6.26335C6.82128 6.3069 6.80336 6.35378 6.79252 6.40252C6.78029 6.45747 6.78029 6.51537 6.78029 6.63116V9.36948C6.78029 9.46963 6.78029 9.5197 6.76414 9.563C6.74986 9.60125 6.72665 9.63554 6.69643 9.663C6.66223 9.69408 6.61574 9.71268 6.52275 9.74987L4.78156 10.4463C4.59333 10.5216 4.49922 10.5593 4.42367 10.5436C4.35761 10.5299 4.29963 10.4906 4.26235 10.4344C4.21971 10.37 4.21971 10.2687 4.21971 10.066V6.63116C4.21971 6.51537 4.21971 6.45747 4.20748 6.40252C4.19664 6.35378 4.17872 6.3069 4.15431 6.26335C4.12678 6.21424 4.08818 6.17109 4.01096 6.0848L1.08871 2.81875Z"
                                fill="#1366FF"
                                stroke="#1366FF"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : (
                        <i
                            className="bi bi-funnel"
                            style={{ fontSize: '0.7em' }}
                            aria-label={`Filter ${column}`}
                        />
                    )}
                </span>
                {activeModal === column && (
                    <FilterModalV2
                        column={column}
                        options={Array.from(new Set(options))}
                        selectedOptions={selectedOptions[column] || []}
                        onApply={handleApplyFilter}
                        onClose={closeModal}
                    />
                )}
            </>
        );
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <p className="page-title float-start card-title ms-4">
                                {getText('msg_88bc3fe3dadd')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_dc6987b83df7')}
                            </p>
                        </div>
                    </div>

                    <div style={{ minHeight: '70vh' }}>
                        {isLoading && (
                            <Spinner
                                width={50}
                                className="pageListloadingSpinner"
                            />
                        )}

                        {!isLoading && sortedReports.length > 0 && (
                            <div className="table-responsive-xxl w-100">
                                <table className="table table-hover align-middle">
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            <th
                                                style={{
                                                    paddingLeft: '20px',
                                                }}
                                            >
                                                <span className="table-column-header-text">
                                                    {getText('msg_5e4a47a52795')}
                                                </span>
                                                {renderSortArrow('name')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText('msg_55f8ebc805e6')}
                                                </span>
                                            </th>
                                            <th
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    width: '1%',
                                                    paddingRight: '20px',
                                                }}
                                            >
                                                <span className="table-column-header-text">
                                                    {getText('msg_075191987a36')}
                                                </span>
                                                {renderSortArrow('lastRunAt')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedReports.map(report => (
                                            <tr key={report.id}>
                                                <td
                                                    style={{
                                                        paddingLeft: '20px',
                                                    }}
                                                >
                                                    <Link
                                                        to={ROUTE_BUILDERS.reportsGenerate(
                                                            report.id,
                                                        )}
                                                        className="titleCell"
                                                    >
                                                        {report.name}
                                                    </Link>
                                                </td>
                                                <td className="subtitleCell">
                                                    {report.description}
                                                </td>
                                                <td
                                                    className="subtitleCell"
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        width: '1%',
                                                        paddingRight: '20px',
                                                    }}
                                                >
                                                    {report.lastRunAt
                                                        ? formatDate(
                                                              report.lastRunAt,
                                                          )
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!isLoading && sortedReports.length === 0 && (
                            <NotFoundBox title={getText('msg_1fd9f3cfed04')} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportsListPage;
